import { Controller } from "@hotwired/stimulus"
import { positionDropdownMenu } from "./dropdown_positioning"

// Drop-in replacement for a plain <select>'s own visual popup, not for the <select> itself: the
// real select (custom_select_target: "select") stays in the DOM with its real name/value/options,
// fully operable by keyboard exactly as before (still tabbable, still fires change, still what
// Capybara's own `select "X", from: ...` acts on in every existing spec, unchanged) and by a
// screen reader (the toggle button below is aria-hidden and unreachable by Tab; the select is the
// only accessible control here). What changed is what a pointer/touch user's tap actually hits.
//
// The bug this exists for: a native <select>'s own popup, on mobile Chrome, mispositions itself
// under a position: sticky ancestor once the page has scrolled (application.html.erb's own top
// navbar + sub-nav are stuck together as one sticky-top unit): Chromium computes the popup's
// anchor against where that sticky ancestor would sit in the unscrolled document flow, not where
// it's actually stuck, so the popup opens up near the sticky bar instead of near the select. Pure
// browser-engine behavior; nothing in our CSS or JS controls a native popup's own rendering, and
// there's no known CSS fix. Confirmed cosmetic-only (the right option was still selected under
// the mispositioned list), but confirmed on a real repro (see the PR this shipped in), not a
// vague theory.
//
// The fix is to stop that popup from ever opening at all: the select sits right under this
// controller's own visible toggle button, opacity: 0 and pointer-events: none, so a tap never
// reaches it and the native popup never has a reason to appear. The toggle instead opens a
// regular Bootstrap dropdown-menu built from the select's own <option> elements, positioned by
// hand the same way sub_nav_scroll_controller's own dropdowns are (see dropdown_positioning.js);
// that's a menu this app fully renders and controls, so it can't inherit a native popup's own
// positioning bug in the first place.
export default class extends Controller {
  static targets = ["select", "toggle", "menu"]

  connect() {
    this.onSelectChange = this.onSelectChange.bind(this)
    this.buildMenu()
    this.syncToggleFromSelect()

    this.selectTarget.addEventListener("change", this.onSelectChange)
    this.element.addEventListener("show.bs.dropdown", () => {
      positionDropdownMenu(this.toggleTarget, this.menuTarget)
    })
  }

  disconnect() {
    this.selectTarget.removeEventListener("change", this.onSelectChange)
  }

  // Reads the select's own <option>s once at connect time, not on every open; nothing here
  // changes them afterward (these are all server-rendered filter/form selects, not selects whose
  // own option list is itself updated by other JS at runtime).
  buildMenu() {
    this.menuTarget.innerHTML = ""

    Array.from(this.selectTarget.options).forEach((option) => {
      const item = document.createElement("li")
      const link = document.createElement("a")

      link.href = "#"
      link.className = "dropdown-item"
      link.textContent = option.text
      link.dataset.value = option.value
      if (option.disabled) link.classList.add("disabled")

      link.addEventListener("click", (event) => {
        event.preventDefault()
        if (option.disabled) return

        this.selectTarget.value = option.value
        this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
      })

      item.appendChild(link)
      this.menuTarget.appendChild(item)
    })
  }

  onSelectChange() {
    this.syncToggleFromSelect()
  }

  // The toggle's own label always mirrors the select's current value, whether that value changed
  // via a dropdown-item click above or, for a keyboard/screen-reader user, the select's own
  // native change event firing from a completely different interaction.
  syncToggleFromSelect() {
    const selected = this.selectTarget.options[this.selectTarget.selectedIndex]

    this.toggleTarget.textContent = selected ? selected.text : ""

    this.menuTarget.querySelectorAll(".dropdown-item").forEach((item) => {
      item.classList.toggle("active", item.dataset.value === this.selectTarget.value)
    })
  }
}
